import { useCallback, useMemo, useRef, useState } from 'react'

/**
 * Ref-counted sandwich auto-open suppress. Same reason from two mounts stays
 * active until both release.
 */
export function useNotificationSandwichGateController() {
  const countsRef = useRef(new Map<string, number>())
  const [reasons, setReasons] = useState<string[]>([])

  const syncReasons = useCallback(() => {
    const next = Array.from(countsRef.current.keys()).sort()
    setReasons(prev => (prev.length === next.length && prev.every((r, i) => r === next[i]) ? prev : next))
  }, [])

  const acquireSandwichSuppress = useCallback(
    (reason: string) => {
      if (!reason) return
      const current = countsRef.current.get(reason) ?? 0
      countsRef.current.set(reason, current + 1)
      if (current === 0) {
        syncReasons()
      }
    },
    [syncReasons]
  )

  const releaseSandwichSuppress = useCallback(
    (reason: string) => {
      if (!reason) return
      const current = countsRef.current.get(reason) ?? 0
      if (current <= 1) {
        countsRef.current.delete(reason)
        syncReasons()
        return
      }
      countsRef.current.set(reason, current - 1)
    },
    [syncReasons]
  )

  const suppressSandwichAutoOpen = reasons.length > 0

  return useMemo(
    () => ({
      suppressSandwichAutoOpen,
      sandwichSuppressReasons: reasons,
      acquireSandwichSuppress,
      releaseSandwichSuppress,
    }),
    [suppressSandwichAutoOpen, reasons, acquireSandwichSuppress, releaseSandwichSuppress]
  )
}
